import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { investorService, JourneyListItem } from '../../../services/investor.service';
import { setActiveInvestorJourney } from '../../../services/investorJourneyScope';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { useJourneyKycGate } from '../../../hooks/useJourneyKycGate';
import { useSmartUploadEnabled } from '../../../services/featureFlags.service';

/** Badge text: ABANDONED reads as "Discontinued" to the investor. */
const statusLabel = (status?: string): string => {
  const s = (status || 'IN PROGRESS').toUpperCase();
  return s === 'ABANDONED' ? 'DISCONTINUED' : s;
};

/** Discontinue offered only while the journey is still in the investor's hands. */
const canDiscontinue = (item: JourneyListItem): boolean => {
  const s = (item.status || '').toUpperCase();
  return !!item.journeyId && s !== 'COMPLETED' && s !== 'ABANDONED';
};

const statusBadgeClass = (status?: string): string => {
  switch ((status || '').toUpperCase()) {
    case 'COMPLETED':
      return 'bg-[#ecfdf5] text-[#10b981] border border-[#10b981]/20';
    case 'IN REVIEW':
      return 'bg-[#fff8f0] text-[#f59e0b] border border-[#f59e0b]/20';
    case 'ABANDONED':
      return 'bg-[#fef2f2] text-[#ef4444] border border-[#ef4444]/20';
    default:
      return 'bg-[#eff6ff] text-[#3b82f6] border border-[#3b82f6]/20';
  }
};

export const JourneyList: React.FC = () => {
  const navigate = useNavigate();
  // Smart Upload server switch: the "Journey documents" button opens it, so it shows only when on.
  const smartUploadEnabled = useSmartUploadEnabled();
  const [loading, setLoading] = useState(true);
  const [journeys, setJourneys] = useState<JourneyListItem[]>([]);
  // Discontinue flow (user-approved plan 2026-09-04): confirm modal with consequence
  // text + optional reason; Dataverse is notified before anything flips locally.
  const [discontinueTarget, setDiscontinueTarget] = useState<JourneyListItem | null>(null);
  const [discontinueReason, setDiscontinueReason] = useState('');
  const [discontinuing, setDiscontinuing] = useState(false);

  const loadJourneys = async () => {
    try {
      const data = await investorService.getJourneys();
      setJourneys(data || []);
    } catch (error) {
      console.error('[JourneyList] Error fetching journeys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDiscontinue = async () => {
    const item = discontinueTarget;
    if (!item?.journeyId) return;
    setDiscontinuing(true);
    try {
      await investorService.discontinueJourney(item.journeyId, discontinueReason);
      toast.success('Journey discontinued. Your Service Provider has been notified.');
      setDiscontinueTarget(null);
      setDiscontinueReason('');
      await loadJourneys();
    } catch (err: any) {
      toast.error(err?.response?.data?.message
        || 'Could not discontinue the journey. Please try again.');
    } finally {
      setDiscontinuing(false);
    }
  };

  const navigateToJourney = (item: JourneyListItem) => {
    // P3.3: remember which journey the investor opened so per-journey pages (onboarding documents,
    // physical submission) fetch/store against it even when reached later via the sidebar.
    // C8 (audit 2026-08-25): only SET, never clear — a row without a journeyId must not wipe
    // the scope (that silently dropped later step pages back to the PRIMARY journey).
    if (item.journeyId) {
      setActiveInvestorJourney(item.journeyId);
    }
    if (item.actionRoute) {
      navigate(item.actionRoute);
    } else if (item.journeyId) {
      navigate(`/investor/journey/${item.journeyId}`);
    } else {
      navigate('/investor/journey');
    }
  };

  // Benefit nudge + per-journey consent + address reconcile (shared with the dashboard).
  const { openJourney, modals } = useJourneyKycGate(navigateToJourney, 'JourneyList');

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-lg w-full overflow-hidden border border-slate-200">
      <div 
        className="text-white px-3 py-3"
        style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
      >
        <h3 className="text-[15px] font-bold text-white mb-0.5 tracking-tight">My Onboarding Journey</h3>
        <p className="text-[10px] text-white/80 m-0 font-medium">
          Track your product applications, schemes, and plans. Select a journey to continue.
        </p>
      </div>

      <div className="bg-[#e1e4e7] p-0">
        <div className="bg-white border border-slate-200/60 p-3 shadow-sm">
          {journeys.length === 0 ? (
            <div className="py-10 text-center">
              <i className="bi bi-signpost-2 text-[28px] text-slate-300"></i>
              <p className="text-[12px] text-slate-500 mt-2 mb-0">No onboarding journeys assigned yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-[#e2e8f0]">
                    <th className="pb-3.5 px-2">PRODUCT</th>
                    <th className="pb-3.5 px-2">SERVICE PROVIDER</th>
                    <th className="pb-3.5 px-2">PLAN</th>
                    <th className="pb-3.5 px-2">SCHEME</th>
                    <th className="pb-3.5 px-2">STATUS</th>
                    <th className="pb-3.5 px-2 text-right">OPTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {journeys.map((item, idx) => (
                    <tr
                      key={item.journeyId || idx}
                      className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => openJourney(item)}
                    >
                      <td className="py-2 px-2 text-[12px] font-bold text-slate-800">{item.product || '-'}</td>
                      <td className="py-2 px-2 text-[12px] text-slate-500">{item.serviceProviderName || '-'}</td>
                      <td className="py-2 px-2 text-[12px] text-slate-500">{item.plan || '-'}</td>
                      <td className="py-2 px-2 text-[12px] text-slate-500">{item.scheme || '-'}</td>
                      <td className="py-2 px-2">
                        <span className={`text-[8px] font-bold px-2 py-0.5 rounded uppercase tracking-wider inline-block ${statusBadgeClass(item.status)}`}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        {(item.status || '').toUpperCase() === 'ABANDONED' ? (
                          <span className="text-[9px] text-slate-400 font-medium">Discontinued</span>
                        ) : (
                          <div className="inline-flex items-center gap-1.5">
                            {/* D3: this journey's Smart Upload / documents view */}
                            {item.journeyId && smartUploadEnabled === true && (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/investor/documents-center?journeyId=${encodeURIComponent(item.journeyId!)}`); }}
                                title="Journey documents"
                                className="bg-[#b0842b]/10 hover:bg-[#b0842b]/20 text-[#b0842b] border border-[#b0842b]/25 text-[8px] font-extrabold px-2 py-1.5 rounded inline-flex items-center transition-colors cursor-pointer"
                              >
                                <i className="bi bi-file-earmark-arrow-up text-[10px]"></i>
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); openJourney(item); }}
                              className="bg-[#ecfdf5] hover:bg-[#d1fae5] text-[#10b981] border border-[#10b981]/25 text-[8px] font-extrabold px-3 py-1.5 rounded inline-flex items-center gap-2 tracking-wider transition-colors cursor-pointer"
                            >
                              {(item.status || '').toUpperCase() === 'COMPLETED' ? 'VIEW' : 'CONTINUE'}
                              <i className="bi bi-chevron-right text-[7px]"></i>
                            </button>
                            {canDiscontinue(item) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDiscontinueTarget(item); setDiscontinueReason(''); }}
                                title="Stop this application — your Service Provider will be notified"
                                className="bg-[#fef2f2] hover:bg-[#fee2e2] text-[#ef4444] border border-[#ef4444]/25 text-[8px] font-extrabold px-3 py-1.5 rounded inline-flex items-center tracking-wider transition-colors cursor-pointer"
                              >
                                DISCONTINUE
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modals}

      {/* Discontinue confirmation — same consequence-modal pattern as Consent Centre Revoke. */}
      <Modal
        show={!!discontinueTarget}
        onHide={discontinuing ? () => undefined : () => setDiscontinueTarget(null)}
        centered
        backdrop={discontinuing ? 'static' : true}
      >
        <Modal.Header closeButton={!discontinuing}>
          <Modal.Title className="fs-5">Discontinue this journey?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-2">
            {`This closes your "${discontinueTarget?.product || 'product'}" application`}
            {discontinueTarget?.serviceProviderName ? ` with ${discontinueTarget.serviceProviderName}` : ''}
            {`. Your Service Provider will be notified.`}
          </p>
          <p className="small text-muted mb-3">
            Your documents and your other journeys are not affected. This cannot be undone from
            the portal — your Service Provider would need to introduce the product again.
          </p>
          <label className="small fw-semibold mb-1">Reason (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            maxLength={500}
            value={discontinueReason}
            onChange={(e) => setDiscontinueReason(e.target.value)}
            placeholder="Tell us why you are discontinuing (optional)"
            disabled={discontinuing}
          />
        </Modal.Body>
        <Modal.Footer>
          {!discontinuing && (
            <Button variant="outline-secondary" size="sm" onClick={() => setDiscontinueTarget(null)}>
              Cancel
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={handleDiscontinue} disabled={discontinuing}>
            {discontinuing ? 'Discontinuing…' : 'Discontinue journey'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
